import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaTable } from "react-icons/fa";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";

// ── Styling helpers ───────────────────────────────────────────────────────────
const C = {
  get yellow() { return getBrandColors().yellow; },
  get blue() { return getBrandColors().blue; },
};

const pill = (active, isDark) => ({
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "4px 11px", borderRadius: 20, cursor: "pointer", userSelect: "none",
  fontSize: 12, fontWeight: 600, transition: "all 0.14s",
  background: active ? C.yellow : isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.07)",
  color: active ? "#1a1200" : isDark ? "rgba(255,255,255,0.65)" : "rgba(3,78,123,0.75)",
  border: active ? `1px solid ${C.yellow}` : isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.15)",
});

const card = (isDark) => ({
  background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.85)",
  border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.10)",
  borderRadius: 14, padding: "18px 20px",
  backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
});

const label = (isDark) => ({
  fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
  color: isDark ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.55)", marginBottom: 8,
});

const inputSt = (isDark) => ({
  width: "100%", boxSizing: "border-box",
  padding: "8px 11px", borderRadius: 8, fontSize: 13,
  background: isDark ? "rgba(255,255,255,0.07)" : "#f4f7fb",
  border: isDark ? "1px solid rgba(255,255,255,0.13)" : "1px solid rgba(3,78,123,0.15)",
  color: isDark ? "rgba(255,255,255,0.88)" : "#1a2a3a",
  outline: "none",
});

const btn = (primary) => ({
  padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
  fontSize: 13, fontWeight: 700, transition: "opacity 0.14s",
  background: primary ? C.yellow : C.blue,
  color: primary ? "#1a1200" : "#fff",
});

const SORT_DIRS = ["ASC", "DESC"];

// ── Main Component ────────────────────────────────────────────────────────────
export default function DynamicReport() {
  const { isDark } = useTheme();

  const [schema, setSchema]         = useState(null);
  const [reportType, setReportType] = useState("");
  const [selCols, setSelCols]       = useState([]);
  const [filters, setFilters]       = useState([]);   // [{key, value}]
  const [sort, setSort]             = useState([]);   // [{key, dir}]
  const [rows, setRows]             = useState(null);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [tableSort, setTableSort]   = useState({ key: "", dir: "ASC" }); // client-side header sort

  // Fetch schema on mount
  useEffect(() => {
    fetch(`${API_BASE}/dynamic-report/schema`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setSchema(d.schema);
          const first = Object.keys(d.schema)[0];
          if (first) applyType(d.schema, first);
        }
      })
      .catch(() => setError("Failed to load report schema"));
  }, []); // eslint-disable-line

  const applyType = (sch, type) => {
    setReportType(type);
    setSelCols(sch[type].default_columns || []);
    setFilters([]);
    setSort([]);
    setRows(null);
    setTableSort({ key: "", dir: "ASC" });
    setError("");
  };

  const cfg = schema && reportType ? schema[reportType] : null;

  // Toggle a column on/off
  const toggleCol = (key) =>
    setSelCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  // Move column left/right in display order
  const moveCol = (key, dir) => {
    setSelCols(prev => {
      const i = prev.indexOf(key);
      if (i < 0) return prev;
      const next = [...prev];
      const swap = i + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[i], next[swap]] = [next[swap], next[i]];
      return next;
    });
  };

  // Filter management
  const addFilter = () => setFilters(prev => [...prev, { key: cfg.filters[0]?.key || "", value: "" }]);
  const removeFilter = (i) => setFilters(prev => prev.filter((_, idx) => idx !== i));
  const updateFilter = (i, field, val) =>
    setFilters(prev => prev.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  // Sort management
  const addSort = () => {
    const firstCol = selCols[0] || "";
    setSort(prev => [...prev, { key: firstCol, dir: "ASC" }]);
  };
  const removeSort = (i) => setSort(prev => prev.filter((_, idx) => idx !== i));
  const updateSort = (i, field, val) =>
    setSort(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  // Run report
  const runReport = useCallback(async () => {
    if (!reportType || selCols.length === 0) return;
    setLoading(true);
    setError("");
    setTableSort({ key: "", dir: "ASC" });
    try {
      const res = await fetch(`${API_BASE}/dynamic-report/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_type: reportType, selected_columns: selCols, filters, sort }),
      });
      const d = await res.json();
      if (d.success) {
        setRows(d.rows);
        setTotal(d.total);
      } else {
        setError(d.message || "Report failed");
      }
    } catch {
      setError("Network error");
    }
    setLoading(false);
  }, [reportType, selCols, filters, sort]);

  // Download CSV
  const downloadCSV = async () => {
    if (!reportType || selCols.length === 0) return;
    try {
      const res = await fetch(`${API_BASE}/dynamic-report/export/csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_type: reportType, selected_columns: selCols, filters, sort }),
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${(cfg?.label || "report").replace(/ /g,"_")}_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed");
    }
  };

  // Client-side sort on table header click
  const handleHeaderClick = (key) => {
    setTableSort(prev => ({ key, dir: prev.key === key && prev.dir === "ASC" ? "DESC" : "ASC" }));
  };

  const displayRows = useMemo(() => {
    if (!rows || !tableSort.key) return rows;
    return [...rows].sort((a, b) => {
      const av = a[tableSort.key] ?? "";
      const bv = b[tableSort.key] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return tableSort.dir === "DESC" ? -cmp : cmp;
    });
  }, [rows, tableSort]);

  const colDefs = cfg?.columns || [];
  const colMap  = Object.fromEntries(colDefs.map(c => [c.key, c]));

  // ── Render ──────────────────────────────────────────────────────────────────
  const T = isDark;
  const textPrimary   = T ? "rgba(255,255,255,0.88)" : "#1a2a3a";
  const textSecondary = T ? "rgba(255,255,255,0.55)" : "rgba(3,78,123,0.60)";
  const divider       = T ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.09)";

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary, padding: "28px 32px", maxWidth: 1400 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaTable size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T ? "#fff" : C.blue }}>Dynamic Report Builder</div>
          <div style={{ fontSize: 13, color: textSecondary, marginTop: 3 }}>
            Pick a report type, choose your columns and filters, then run or download.
          </div>
        </div>
      </div>

      {!schema ? (
        <div style={{ color: textSecondary }}>Loading schema…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 20, alignItems: "start" }}>

          {/* ── LEFT CONFIG PANEL ─────────────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Report Type */}
            <div style={card(T)}>
              <div style={label(T)}>Report Type</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(schema).map(([key, s]) => (
                  <div
                    key={key}
                    onClick={() => applyType(schema, key)}
                    style={{
                      padding: "9px 13px", borderRadius: 9, cursor: "pointer",
                      fontSize: 13, fontWeight: reportType === key ? 700 : 500,
                      background: reportType === key
                        ? (T ? "rgba(247,206,11,0.18)" : "rgba(247,206,11,0.22)")
                        : "transparent",
                      color: reportType === key ? (T ? C.yellow : "#8a6000") : textSecondary,
                      borderLeft: reportType === key ? `3px solid ${C.yellow}` : "3px solid transparent",
                      transition: "all 0.14s",
                    }}
                  >
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            {cfg && (
              <>
                {/* Column Selector */}
                <div style={card(T)}>
                  <div style={label(T)}>Output Columns</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
                    {colDefs.map(c => (
                      <span key={c.key} style={pill(selCols.includes(c.key), T)} onClick={() => toggleCol(c.key)}>
                        {selCols.includes(c.key) ? "✓ " : ""}{c.label}
                      </span>
                    ))}
                  </div>
                  <div style={{ borderTop: `1px solid ${divider}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 11, color: textSecondary, marginBottom: 6 }}>Column order (drag to reorder):</div>
                    {selCols.map((key, i) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: textPrimary, flex: 1 }}>{colMap[key]?.label || key}</span>
                        <button onClick={() => moveCol(key, -1)} disabled={i === 0}
                          style={{ background: "none", border: "none", cursor: "pointer", color: textSecondary, padding: "0 3px", fontSize: 14 }}>↑</button>
                        <button onClick={() => moveCol(key, 1)} disabled={i === selCols.length - 1}
                          style={{ background: "none", border: "none", cursor: "pointer", color: textSecondary, padding: "0 3px", fontSize: 14 }}>↓</button>
                        <button onClick={() => toggleCol(key)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e", padding: "0 3px", fontSize: 14 }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Filters */}
                <div style={card(T)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={label(T)}>Filters</div>
                    <button onClick={addFilter} style={{ ...btn(false), padding: "4px 10px", fontSize: 12 }}>+ Add Filter</button>
                  </div>
                  {filters.length === 0 && (
                    <div style={{ fontSize: 12, color: textSecondary }}>No filters — showing all records</div>
                  )}
                  {filters.map((f, i) => {
                    const fd = cfg.filters.find(x => x.key === f.key) || cfg.filters[0];
                    return (
                      <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <select
                            value={f.key}
                            onChange={e => updateFilter(i, "key", e.target.value)}
                            style={{ ...inputSt(T), marginBottom: 5 }}
                          >
                            {cfg.filters.map(fi => <option key={fi.key} value={fi.key}>{fi.label}</option>)}
                          </select>
                          {fd?.type === "select" ? (
                            <select value={f.value} onChange={e => updateFilter(i, "value", e.target.value)} style={inputSt(T)}>
                              <option value="">— All —</option>
                              {(fd.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          ) : (
                            <input
                              type={fd?.type === "date" ? "date" : "text"}
                              value={f.value}
                              placeholder={fd?.type === "text" ? "contains…" : ""}
                              onChange={e => updateFilter(i, "value", e.target.value)}
                              style={inputSt(T)}
                            />
                          )}
                        </div>
                        <button onClick={() => removeFilter(i)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e", fontSize: 18, paddingTop: 6 }}>×</button>
                      </div>
                    );
                  })}
                </div>

                {/* Sort Config */}
                <div style={card(T)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={label(T)}>Sort Order</div>
                    <button onClick={addSort} style={{ ...btn(false), padding: "4px 10px", fontSize: 12 }}>+ Add Sort</button>
                  </div>
                  {sort.length === 0 && (
                    <div style={{ fontSize: 12, color: textSecondary }}>Default sort applied. Click column headers in results to sort.</div>
                  )}
                  {sort.map((s, i) => (
                    <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
                      <select value={s.key} onChange={e => updateSort(i, "key", e.target.value)} style={{ ...inputSt(T), flex: 2 }}>
                        {colDefs.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <select value={s.dir} onChange={e => updateSort(i, "dir", e.target.value)} style={{ ...inputSt(T), flex: 1 }}>
                        {SORT_DIRS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <button onClick={() => removeSort(i)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e53e3e", fontSize: 18 }}>×</button>
                    </div>
                  ))}
                </div>

                {/* Run / Download */}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={runReport} disabled={loading || selCols.length === 0} style={{ ...btn(true), flex: 1 }}>
                    {loading ? "Running…" : "▶  Run Report"}
                  </button>
                  {rows !== null && (
                    <button onClick={downloadCSV} style={{ ...btn(false), flex: 1 }}>
                      ↓  Download CSV
                    </button>
                  )}
                </div>

                {error && (
                  <div style={{ background: "rgba(229,62,62,0.12)", border: "1px solid rgba(229,62,62,0.30)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#e53e3e" }}>
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── RIGHT RESULTS PANEL ───────────────────────────────────────── */}
          <div>
            {rows === null ? (
              <div style={{ ...card(T), textAlign: "center", padding: "60px 20px", color: textSecondary }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Configure and run your report</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Select columns, add filters, then click Run Report</div>
              </div>
            ) : (
              <div style={card(T)}>
                {/* Results header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{cfg?.label}</span>
                    <span style={{ fontSize: 13, color: textSecondary, marginLeft: 10 }}>
                      {total.toLocaleString()} row{total !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {tableSort.key && (
                      <span style={{ fontSize: 11, color: textSecondary }}>
                        Sorted by {colMap[tableSort.key]?.label || tableSort.key} {tableSort.dir}
                      </span>
                    )}
                    <button onClick={downloadCSV} style={{ ...btn(false), padding: "6px 14px", fontSize: 12 }}>↓ CSV</button>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {selCols.map(key => (
                          <th
                            key={key}
                            onClick={() => handleHeaderClick(key)}
                            style={{
                              padding: "9px 12px", textAlign: "left", cursor: "pointer",
                              background: T ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)",
                              borderBottom: `2px solid ${T ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.15)"}`,
                              fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                              color: tableSort.key === key ? C.yellow : textSecondary,
                              whiteSpace: "nowrap", userSelect: "none",
                            }}
                          >
                            {colMap[key]?.label || key}
                            {tableSort.key === key ? (tableSort.dir === "ASC" ? " ↑" : " ↓") : " ⇅"}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={selCols.length} style={{ padding: "32px 12px", textAlign: "center", color: textSecondary }}>
                            No results match your filters
                          </td>
                        </tr>
                      ) : displayRows.map((row, ri) => (
                        <tr key={ri} style={{
                          background: ri % 2 === 0 ? "transparent" : (T ? "rgba(255,255,255,0.025)" : "rgba(3,78,123,0.025)"),
                        }}>
                          {selCols.map(key => (
                            <td key={key} style={{
                              padding: "8px 12px",
                              borderBottom: `1px solid ${T ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.06)"}`,
                              color: textPrimary, whiteSpace: "nowrap",
                            }}>
                              {row[key] === null || row[key] === undefined ? (
                                <span style={{ color: textSecondary }}>—</span>
                              ) : String(row[key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
